import { useEffect, useState, useContext } from "react";
import {
  Button,
  Result,
  Typography,
  Tabs,
  Select,
} from "antd";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import { PlusOutlined } from "@ant-design/icons";
import { usersListApi } from "../../../services/auth.services";
import UsersTable from "./UsersTable";
import btnStyles from "../../../styles/Custom/Button.module.css";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import CustomLayout from "../../Layout/CustomLayout";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import UsersForm from "./UsersForm";
import ReceptionistSideMenu from "../../Layout/CustomLayout/ReceptionistSideMenu";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";
import { UiDataTable } from "../../ui";
import pageStyles from "../../../styles/Custom/Dashboard/UsersPage.module.css";
import inputStyles from "../../../styles/Custom/Input.module.css";

function Users() {
  const navigate = useNavigate();
  const params = useQuery();
  const [activeSegment, setActiveSegment] = useState(params?.role || "all");
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => parseSearchParam(params));
  const [page, setPage] = useState(() => parsePageParam(params));
  const { userDetails } = useContext(UserContext);
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const isReceptionist = defaultRole === "Receptionist";

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    setLoading(true);
    const roleParam = params?.role !== "all" && params?.role;

    const updatedRole =
      roleParam === "admins"
        ? ["Organization_Owner", "Organization_Admin", "Facility_Admin"]
        : roleParam === "practioners"
        ? [
            "Doctor",
            "Nurse",
            "Speech_Swallow_Therapist",
            "Respiratory_Therapist",
            "Nutritionist",
            "Physiotherapist",
          ]
        : roleParam === "receptionist"
        ? ["Receptionist"]
        : undefined;

    const queryParams = {
      roles: updatedRole,
      search: searchQuery || undefined, // Avoid sending empty string
      page: page,
    };

    usersListApi(queryParams)
      .then((res) => {
        setUsers(res?.data); // assuming paginated response
        setFilteredData(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [params?.role, searchQuery, page, refresh]);

  const onChange = (value) => {
    setPage(1);
    setActiveSegment(value);
    setSearchQuery(null);
    updateUrlSearchParams(navigate, {
      role: value === "all" ? null : value,
      search: null,
      page: null,
    });
  };

  const handleSearch = (e) => {
    const query = e?.target?.value;
    setPage(1);
    setSearchQuery(query || null);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPage(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };
  const roles = [
    { label: "Admins", key: "admins" },
    { label: "Practitioners", key: "practioners" },
    { label: "Receptionist", key: "receptionist" },
  ];

  const tabItems = [{ label: "All", key: "all" }, ...roles];
  const roleFilterOptions = tabItems.map(({ label, key }) => ({
    label,
    value: key,
  }));

  useEffect(() => {
    setActiveSegment(params?.role || "all");
  }, [params?.role]);

  const headerTabs = (
    <Tabs
      className={pageStyles.usersHeaderTabs}
      items={tabItems}
      onChange={onChange}
      activeKey={activeSegment}
      moreIcon={null}
    />
  );

  return (
    <CustomLayout
      layoutClassName={
        isReceptionist ? "reception-layout reception-users-page" : ""
      }
      header={
        <div className={`${pageStyles.usersPageHeader} users-page-header`}>
          <Typography className={typoStyles.h6}>Users</Typography>
          <div className={pageStyles.usersHeaderTabsDesktop}>{headerTabs}</div>
        </div>
      }
      sideContent={
        isReceptionist ? <ReceptionistSideMenu /> : <SideMenu />
      }

    >
      <div className={pageStyles.usersMobileFilterWrap}>
        <Select
          className={inputStyles.mdSelect}
          value={activeSegment}
          onChange={onChange}
          options={roleFilterOptions}
          popupMatchSelectWidth
        />
      </div>
      {users ? (
        <UiDataTable
          showHeader={false}
          shellClassName={
            isReceptionist
              ? `ui-table-fill-shell ${pageStyles.usersTableShell}`
              : "ui-table-fill-shell"
          }
          onSearch={handleSearch}
          searchPlaceholder="Search user"
          toolbarTrailing={
            !isReceptionist ? (
              <Button
                type="primary"
                onClick={() => setOpen(true)}
                className={btnStyles.lgBtn}
              >
                <PlusOutlined /> User
              </Button>
            ) : null
          }
        >
          <UsersTable
            data={users?.data}
            loading={loading}
            refreshPage={refreshPage}
            isReceptionist={isReceptionist}
            page={page}
            setPage={handlePageChange}
            totalCount={users?.total_count}
          />
        </UiDataTable>
      ) : (
        !loading && <Result title="No users" />
      )}
      {open && (
        <UsersForm
          isVisible={open}
          setIsVisible={setOpen}
          refreshPage={refreshPage}
        />
      )}
    </CustomLayout>
  );
}

export default Users;
